"use client";

import React, { useContext, useEffect, useRef, useState } from "react";
import {
  Box,
  Button,
  CircularProgress,
  Tab,
  Tabs,
  Typography,
} from "@mui/material";
import { useAppTranslate } from "src/hooks/useAppTranslate";
import { productApi, productTagApi } from "src/services/api";
import {
  GetProductDto,
  GetProductTagDto,
  ProductApi,
} from "src/services/generated-api";
import SingleProduct, { ProductWithAttributes } from "./single-product";
import { useLocale } from "next-intl";
import { ColorModeContext } from "@contexts/index";
import ResponsiveTabs from "./responsive-tabs";
import { getCurrencyBasedOnDomain } from "src/utils/domain.util";
import getProductPrice from "src/utils/get-product-price.util";
import * as Sentry from "@sentry/nextjs";
import { useSearchParams } from "next/navigation";
import { highlightPlacement } from "./highlight";
interface Props {
  disable: boolean;
  addProduct?: (productId: string, quantity: number) => Promise<void>;
  /** Set an existing line's quantity; 0 removes it. Undefined disables the minus button. */
  setProductQuantity?: (productId: string, quantity: number) => Promise<void>;
  cartQuantities?: Record<string, number>;
}

const ProductStore: React.FC<Props> = ({
  disable,
  addProduct,
  setProductQuantity,
  cartQuantities,
}) => {
  const translate = useAppTranslate();
  const currentLocale = useLocale();
  const [allProducts, setAllProducts] = useState<GetProductDto[]>([]);
  const [tags, setTags] = useState<GetProductTagDto[]>([]);
  const [tag, setTag] = useState<GetProductTagDto | null>(null);
  const [products, setProducts] = useState<GetProductDto[]>([]);
  const [showMore, setShowMore] = useState<boolean>(false);
  const [featuredProducts, setFeaturedProducts] = useState<GetProductDto[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  let language = currentLocale === "sl" ? "si" : currentLocale || "en";
  language = language.split("-")[0];
  const { mode, setMode } = useContext(ColorModeContext);
  // A bundle slot links to `?product=<id>#izdelki`: that product's tab, on
  // screen, with its dialog open. Applied once per id, so the customer can
  // still change tabs afterwards.
  const highlightedProductId = useSearchParams()?.get("product") ?? null;
  const [placedProductId, setPlacedProductId] = useState<string | null>(null);

  async function fetchAllProducts(
    productApi: ProductApi,
    activeFilter: string[]
  ) {
    let allProductsFetch: GetProductDto[] = [];
    let page = 1;
    let totalPages = 0;

    do {
      // Attempt to fetch the current page
      const { data } = await productApi.getManyBaseProductControllerProduct(
        undefined,
        undefined,
        activeFilter,
        undefined,
        undefined,
        undefined,
        300,
        undefined,
        page
      );
      // console.log(data.data, "data", data.pageCount, "pageCount", page, "page");
      // Add the products from the current page to the allProducts array
      allProductsFetch = allProductsFetch.concat(data.data);

      // Update totalPages based on the response
      totalPages = data.pageCount;
      // Increment the page number for the next request
      page++;
    } while (page <= totalPages); // Continue until all pages are fetched
    return allProductsFetch;
  }

  const fetchProducts = async () => {
    setIsLoading(true);
    setError(null);
    try {
      const activeFilter = [`active||eq||true`, `nOfUnits||gt||0`];
      // Side by side: the product query does not depend on the tags, and the
      // tiles are the first thing a visitor waits for on the landing page.
      const [{ data: dataTag }, data] = await Promise.all([
        productTagApi.getManyBaseProductTagControllerProductTag(),
        fetchAllProducts(productApi, activeFilter),
      ]);
      const tagsSorted = dataTag.data.sort((a, b) => a.rank - b.rank);
      setTags(tagsSorted);
      const tagSelected = tagsSorted[0];
      // find tag with lowest rank
      setTag(tagSelected);
      // // remove duplicates with the same id
      // const uniqueData = data.filter(
      //   (item, index, self) => index === self.findIndex((t) => t.id === item.id)
      // );
      const currency = getCurrencyBasedOnDomain();
      const productsFiltered = data
        .filter((p) => p.nOfUnits > 0 && getProductPrice(p, currency))
        // rank first, oldest first within a rank. `featured` still decides
        // which products appear before "show more" (see the `featuredProducts`
        // filter below) — it is a selection flag, not an ordering one, and
        // using it for both is why 90 flagged products produced no useful
        // order. `createdAt` isn't on GetProductDto but is on the wire (see
        // the ProductWithAttributes comment in single-product.tsx), so both
        // operands are cast, not just the ones read for `rank`.
        .sort((a, b) => {
          const pa = a as ProductWithAttributes;
          const pb = b as ProductWithAttributes;
          const byRank = (pa.rank ?? 0) - (pb.rank ?? 0);
          if (byRank !== 0) return byRank;
          const byCreatedAt =
            new Date(pa.createdAt ?? 0).getTime() -
            new Date(pb.createdAt ?? 0).getTime();
          if (byCreatedAt !== 0) return byCreatedAt;
          // Third tie-break to match the backend's @Crud sort
          // (product.controller.ts): createdAt defaults to
          // CURRENT_TIMESTAMP, transaction time, so a future bulk insert
          // could give every row an identical timestamp. `id` (a UUID) is
          // always unique, making this total by construction.
          return (pa.id ?? "").localeCompare(pb.id ?? "");
        });
      // add products baesd on tag to allProducts

      setAllProducts(productsFiltered);
      data.forEach((product) => {
        const price = getProductPrice(product, currency);
        if (!price) return;
        // ProductContext has been removed - prices and names are no longer cached
        // updatePrice(product.id, price);
        // updateName(product.id, product.name);
      });
    } catch (error) {
      setError("Failed to fetch products");
      Sentry.captureException(error);
      // Log the error or display a message to the user
    } finally {
      setIsLoading(false);
    }
  };
  useEffect(() => {
    if (tag && allProducts.length > 0) {
      const productsFromTag = allProducts.filter((product) => {
        return product.tags.some((productTag) => productTag.id === tag.id);
      });
      setProducts(productsFromTag);
      setFeaturedProducts(
        productsFromTag.filter((product) => product.featured === true)
      );
    }
  }, [tag, allProducts]);
  useEffect(() => {
    fetchProducts();
  }, []);
  // Adjusted while rendering, not in an effect (react.dev "adjusting state
  // when a prop changes"): the first render that has the products moves there.
  if (highlightedProductId && placedProductId !== highlightedProductId) {
    const placement = highlightPlacement(allProducts, tags, highlightedProductId);
    if (placement) {
      setPlacedProductId(highlightedProductId);
      setTag(tags.find((t) => t.id === placement.tagId) ?? null);
      if (placement.showMore) setShowMore(true);
    }
  }
  // The browser's own #izdelki jump happened before the products loaded (this
  // component renders nothing until then), so it is redone once placed.
  useEffect(() => {
    if (!placedProductId) return;
    document
      .getElementById("izdelki")
      ?.scrollIntoView?.({ behavior: "smooth", block: "start" });
  }, [placedProductId]);
  const handleSelectTag = (tagId: string) => {
    const tagSelected = tags.find((tag) => tag.id === tagId);
    setTag(tagSelected || null);
  };
  const handleChangeTag = (event: React.SyntheticEvent, newValue: string) => {
    const tagSelected = tags.find((tag) => tag.id === newValue);
    setTag(tagSelected || null);
  };

  if (isLoading) return <></>;
  if (error) return <Typography color="error">{error}</Typography>;

  return (
    <Box id="izdelki" sx={{ pb: { xs: 12, sm: 14 } }}>
      <ResponsiveTabs
        tags={tags}
        selectedTag={tag}
        handleChangeTag={handleChangeTag}
        mode={mode}
        language={language}
      />

      {products.length > 0 && (
        <>
          <Box
            sx={{
              display: "flex",
              flexWrap: "wrap", // Wrap products if they don't fit in a single row
              gap: 2,
              // "center" let cards of different heights (facts.attributes and
              // the fit chip are both conditional) sit at whatever height
              // they individually needed, so neighbouring cards' price/button
              // rows landed at different y positions. "stretch" plus
              // height: "100%" on the card's Paper (single-product.tsx) lets
              // flexGrow/mt:"auto" inside the card push price and button to a
              // shared baseline.
              alignItems: "stretch",
              justifyContent: "center", // Center products for better aesthetics
            }}
          >
            {showMore || featuredProducts.length === 0
              ? products.map((product) => (
                  <SingleProduct
                    key={product.id}
                    product={product}
                    addProduct={addProduct}
                    setProductQuantity={setProductQuantity}
                    cartQuantities={cartQuantities}
                    autoOpen={product.id === highlightedProductId}
                  />
                ))
              : featuredProducts.map((product) => (
                  <SingleProduct
                    key={product.id}
                    product={product}
                    addProduct={addProduct}
                    setProductQuantity={setProductQuantity}
                    cartQuantities={cartQuantities}
                    autoOpen={product.id === highlightedProductId}
                  />
                ))}
          </Box>
          <Box sx={{ display: "flex", justifyContent: "center" }}>
            <Button
              type="button"
              onClick={() => setShowMore((prevShowMore) => !prevShowMore)}
              disabled={disable}
              sx={{ m: 1 }}
            >
              {showMore
                ? translate("product.showLess")
                : translate("product.showMore")}
            </Button>
          </Box>
        </>
      )}

    </Box>
  );
};

export default ProductStore;
